import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import IKit_Builder_Option from "../../../../../../models/IKit_Builder_Option";
import INewKIt_Builder_Option from "../../../../../../models/INewKIt_Builder_Option";
import { OwAccordion } from "../../../../../shared/OwAccordion/OwAccordion";
import { OwFnInput } from "../../../../../shared/OwFnInput/OwFnInput";
import OwLabel from "../../../../../shared/OwLabel/OwLabel";
import OwCheckbox from "../../../../../shared/OwCheckbox/OwCheckbox";
import OwButton from "../../../../../shared/OwButton/OwButton";
import Typeahead from "../../../../../shared/Typeahead/Typeahead";
import ITypeaheadCatalogItemDTO from "../../../../../../models/DTO/ITypeaheadCatalogItemDTO";
import UserContext from "../../../../../../contexts/UserContext";
import SupplierContext from "../../../../../../contexts/SupplierContext";
import ICatalog_Item from "../../../../../../models/ICatalog_Item";
import { v4 } from "uuid";
import IOnTypeaheadCatalogItemDTO from "../../../../../../models/DTO/IOnTypeaheadCatalogItemDTO";
import { SocketContext } from "../../../../../../contexts/SocketContext";
import IUiSocketSetup from "../../../../../../models/IUiSocketSetup";
import EndpointsEnum from "../../../../../../models/EndpointsEnum";
import IKit_Builder_Option_Item from "../../../../../../models/IKit_Builder_Option_Item";
import INewKit_Builder_Option_Item from "../../../../../../models/INewKit_Builder_Option_Item";
import { CatalogItemKitBuilderOptionItem } from "./CatalogItemKitBuilderOptionItem/CatalogItemKitBuilderOptionItem";
import IKit_Builder_Option_ItemUI from "../../../../../../models/IKit_Builder_Option_ItemUI";
import IKit_Builder_OptionUI from "../../../../../../models/IKit_Builder_OptionUI";
import { useSortable, arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    DragOverlay,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import "./CatalogItemKitBuilderOption.scss";
import OwToggle from "@components/shared/OwToggle/OwToggle";

type Props = {
    option: IKit_Builder_OptionUI | Partial<IKit_Builder_OptionUI>;
    catalogGuid: string;
    updateFn: (option: IKit_Builder_OptionUI | Partial<IKit_Builder_OptionUI>) => void;
    openOverride?: boolean;
    id: string;
    isActive?: boolean;
    isDragOverlay?: boolean;
};

export const CatalogItemKitBuilderOption = (props: Props) => {
    // Always call the hook, but we'll conditionally use its values
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: props.id,
        // Explicitly specify drag handle
        data: {
            type: "kit-builder-option",
        },
    });

    // Conditionally apply the styles based on whether this is a drag overlay
    const style = props.isDragOverlay
        ? {
              position: "relative" as const,
              zIndex: 1000,
              opacity: 0.9,
          }
        : {
              transform: CSS.Transform.toString(transform),
              transition,
              opacity: isDragging ? 0.6 : 1,
              position: "relative" as const,
              zIndex: isDragging ? 1 : "auto",
          };

    // Styles for when active (being dragged)
    const activeStyles = props.isActive
        ? {
              background: "#f7f9fc",
              boxShadow: "0 0 10px rgba(0, 0, 0, 0.15)",
              border: "1px dashed #007bff",
          }
        : {};

    // Style for drag overlay
    const overlayStyles = props.isDragOverlay
        ? {
              background: "#f7f9fc",
              boxShadow: "0 5px 16px rgba(0, 0, 0, 0.2)",
              opacity: 0.9,
              border: "1px solid #007bff",
              width: "100%",
          }
        : {};

    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;

    const [editMode, setEditMode] = useState(!("id" in props.option));
    const [activeItemId, setActiveItemId] = useState<string | null>(null);
    const [activeItem, setActiveItem] = useState<IKit_Builder_Option_ItemUI | Partial<IKit_Builder_Option_ItemUI> | null>(null);
    const [openAllItems, setOpenAllItems] = useState(false);
    const [accordionStates, setAccordionStates] = useState<{ [key: string]: boolean }>({});

    // Configure sensors for drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const getKitOptionItems = (): IKit_Builder_Option_ItemUI[] => {
        const items = (props.option.Kit_Builder_Option_Items || []).map((item, index) => {
            const updatedItem = {
                ...item,
                uuid: item.uuid || v4(),
                sequence: item.sequence !== undefined ? item.sequence : index,
            } as IKit_Builder_Option_ItemUI;

            return updatedItem;
        });

        return items;
    };

    const [foundCatalogItems, setFoundCatalogItems] = useState<ICatalog_Item[]>([]);
    const [selectedItem, setSelectedItem] = useState<ICatalog_Item | undefined>();
    const [clearTypeahead, setClearTypeahead] = useState<boolean>(false);
    const [kitOptionItems, setKitOptionItems] = useState<IKit_Builder_Option_ItemUI[]>(getKitOptionItems());

    const onTypeheadCatalogItems = (data: IOnTypeaheadCatalogItemDTO) => {
        if (data.state === props.option.uuid) {
            setFoundCatalogItems(data.catalog_items);
        }
    };

    useEffect(() => {
        setKitOptionItems(getKitOptionItems());
    }, [props.option.Kit_Builder_Option_Items]);

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, fn: onTypeheadCatalogItems }];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                socket.off(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        const name = e.target.name;

        const updatedOption = {
            ...props.option,
            [name]: value,
        };

        // setOption(updatedOption);
        props.updateFn(updatedOption);
    };

    const markForDelete = () => {
        const updatedOption: Partial<IKit_Builder_OptionUI> = {
            ...props.option,
            deleteFlag: !props.option.deleteFlag,
        };

        props.updateFn(updatedOption);
    };

    const leftElemDetails = () => {
        if (editMode) {
            return (
                <div className={"d-flex align-items-center"}>
                    <i className={"fas fa-check text-success pointer me-2"} onClick={() => setEditMode(false)} />
                    <i className={"fas fa-times text-danger pointer me-2"} onClick={() => setEditMode(false)} />
                    <OwFnInput fn={handleChange} name={"name"} value={props.option.name} />
                </div>
            );
        } else {
            return (
                <div className={"d-flex align-items-center"}>
                    <div className="drag-handle me-2" {...listeners}>
                        <i className="fas fa-grip-lines" />
                    </div>
                    <i className={"fas fa-pencil-alt text-primary pointer me-2"} onClick={() => setEditMode(true)} />
                    <i
                        className={"fas text-danger pointer me-2 " + (props.option.deleteFlag ? "fa-undo" : "fa-trash-alt")}
                        onClick={() => {
                            markForDelete();
                        }}
                    />
                    <span className={props.option.deleteFlag ? "text-strikethrough text-danger" : ""}>{props.option.name}</span>
                </div>
            );
        }
    };

    const findItems = (name: string) => {
        const data: ITypeaheadCatalogItemDTO = {
            name,
            catalogGuid: props.catalogGuid,
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            state: props.option.uuid,
        };

        socket.emit(EndpointsEnum.CatalogItemTypeaheadCatalogItem, data);
    };

    const clearTypeaheadFn = () => {
        setClearTypeahead(false);
        setSelectedItem(undefined);
    };

    useEffect(() => {
        if (selectedItem) {
            const newItemOption: Partial<IKit_Builder_Option_ItemUI> = {
                catalog_item_id: selectedItem.id,
                require_sub_option: false,
                Catalog_Item: selectedItem,
                uuid: v4(),
            };

            const optionItems: (IKit_Builder_Option_ItemUI | Partial<IKit_Builder_Option_ItemUI>)[] = [newItemOption, ...kitOptionItems];

            const updatedOption = {
                ...props.option,
                Kit_Builder_Option_Items: optionItems,
            };

            props.updateFn(updatedOption);

            setSelectedItem(undefined);
            setClearTypeahead(true);
        }
    }, [selectedItem]);

    const selectItem = (item: ICatalog_Item) => {
        if (item) {
            setSelectedItem(item);
        }
    };

    const updateFn = (optionItem: IKit_Builder_Option_ItemUI | Partial<IKit_Builder_Option_ItemUI>) => {
        const updatedItems = kitOptionItems.map((item) => {
            if (item.uuid === optionItem.uuid) {
                return optionItem;
            } else {
                return item;
            }
        });

        const updatedOption = {
            ...props.option,
            Kit_Builder_Option_Items: updatedItems,
        };

        props.updateFn(updatedOption);
    };

    const removeOptionItemFn = (optionItem: IKit_Builder_Option_ItemUI | Partial<IKit_Builder_Option_ItemUI>) => {
        const optionItemIndex = kitOptionItems?.findIndex((item) => item.uuid === optionItem.uuid);

        if (optionItemIndex !== undefined) {
            let optionItems = kitOptionItems || [];

            // if the item has an id then we set the delete flag to true, otherwise we just remove it from the array.
            if (optionItem.id) {
                // update subKitOptionItems array
                optionItems[optionItemIndex].deleteFlag = !optionItems[optionItemIndex].deleteFlag;
            } else {
                optionItems = [...optionItems.slice(0, optionItemIndex), ...optionItems.slice(optionItemIndex + 1)];
            }

            const updatedOption = {
                ...props.option,
                Kit_Builder_Option_Items: optionItems,
            };

            props.updateFn(updatedOption);
        }
    };

    // Removed useEffect - accordion states are now set directly in openAllItemsFn

    const openAllItemsFn = () => {
        const newOpenState = !openAllItems;
        setOpenAllItems(newOpenState);

        // Directly set accordion states when button is clicked
        const newStates: { [key: string]: boolean } = {};
        kitOptionItems.forEach((item) => {
            if (item.uuid) {
                newStates[item.uuid] = newOpenState;
            }
        });
        console.log("newStates", newStates);
        setAccordionStates(newStates);
    };

    // Handle drag start for option items
    const handleItemDragStart = (event: DragStartEvent) => {
        const { active } = event;
        setActiveItemId(active.id as string);

        // Find the active item to display in drag overlay
        const item = kitOptionItems.find((item) => item.uuid === active.id);
        if (item) {
            setActiveItem(item);
        }
    };

    // Handle drag end for option items - reorder the list
    const handleItemDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = kitOptionItems.findIndex((item) => item.uuid === active.id);
            const newIndex = kitOptionItems.findIndex((item) => item.uuid === over.id);

            const updatedArr = arrayMove(kitOptionItems, oldIndex, newIndex);

            // Update sequence for all items
            const reOrderedArr = updatedArr.map((item, index) => {
                return { ...item, sequence: index };
            });

            const updatedOption = {
                ...props.option,
                Kit_Builder_Option_Items: reOrderedArr,
            };

            props.updateFn(updatedOption);
        }

        setActiveItemId(null);
        setActiveItem(null);
    };

    const accordionToggledFn = (item: IKit_Builder_Option_ItemUI | Partial<IKit_Builder_Option_ItemUI>) => {
        if (item.uuid) {
            console.log("accordionToggledFn", item.uuid);
            setAccordionStates((prevStates) => ({
                ...prevStates,
                [item.uuid as string]: !prevStates[item.uuid as string],
            }));
        }
    };

    const handleCopySubOptions = (targetItemUuid: string, sourceItemUuid: string, mode: "merge" | "replace") => {
        const sourceItem = kitOptionItems.find((item) => item.uuid === sourceItemUuid);
        const targetItem = kitOptionItems.find((item) => item.uuid === targetItemUuid);

        if (!sourceItem?.Sub_Kit_Builder_Option_Items || !targetItem) return;

        // Clone sub-options with new UUIDs, clear IDs (mark as new)
        const existingSubOptions = targetItem.Sub_Kit_Builder_Option_Items || [];
        const clonedSubOptions = sourceItem.Sub_Kit_Builder_Option_Items.filter((sub) => !sub.deleteFlag).map((subItem, index) => ({
            ...subItem,
            id: undefined, // New item
            uuid: v4(),
            parent_option_item_id: targetItem.id,
            sequence: mode === "merge" ? existingSubOptions.length + index : index,
        }));

        const finalSubOptions = mode === "merge" ? [...existingSubOptions, ...clonedSubOptions] : clonedSubOptions;

        const updatedTargetItem = {
            ...targetItem,
            Sub_Kit_Builder_Option_Items: finalSubOptions,
        };

        updateFn(updatedTargetItem);
    };

    return (
        <div
            className={"CatalogItemKitBuilderOption"}
            ref={setNodeRef}
            style={{ ...style, ...activeStyles, ...overlayStyles }}
            {...attributes}>
            <OwAccordion leftElem={leftElemDetails()} initOpen={editMode} chevronSide={"RIGHT"} openOverride={props.openOverride}>
                <div className={"ms-4"}>
                    <div className={"d-flex mb-2"}>
                        <div className={"d-flex align-items-center me-4"}>
                            <OwFnInput fn={handleChange} name={"min_required"} value={props.option.min_required} className={"w-7r"} />
                            <OwLabel title={"min_required"} className={"ms-2"} />
                        </div>

                        <div className={"d-flex align-items-center"}>
                            <OwFnInput fn={handleChange} name={"max_allowed"} value={props.option.max_allowed} className={"w-7r"} />
                            <OwLabel title={"max_allowed"} className={"ms-2"} />
                        </div>
                    </div>
                    <div className={"mb-2"}>
                        <OwToggle
                            label={"allow_repeats"}
                            name={"allow_repeats"}
                            active={props.option.allow_repeats || false}
                            fn={handleChange}
                            labelOnRight={true}
                        />
                    </div>

                    <div>
                        <hr />
                    </div>

                    <div>
                        <div className={"d-flex align-items-center mb-2"}>
                            <OwLabel title={"option_items"} className={"me-auto"} />
                            <OwButton
                                title={openAllItems ? "close_all" : "open_all"}
                                className={"btn-sm btn-primary no-wrap"}
                                leftIconClass={"fas fa-eye"}
                                fn={openAllItemsFn}
                            />
                        </div>
                        <Typeahead
                            isSelectableFn={() => true}
                            typeaheadApiFn={findItems}
                            confirmAdd={true}
                            typeaheadItems={foundCatalogItems}
                            title={"name"}
                            titleProp={"name"}
                            subTitleProp={"sku"}
                            selectedItemFn={selectItem}
                            clearTypeaheadCallback={clearTypeaheadFn}
                            clearTypeahead={clearTypeahead}
                            selectedItem={selectedItem}
                            selectedItemTitleProp={"name"}
                            placeholder={"search"}
                        />
                    </div>

                    <div className={"mt-2"}>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragStart={handleItemDragStart}
                            onDragEnd={handleItemDragEnd}>
                            <SortableContext
                                items={kitOptionItems.map((item) => item.uuid as string)}
                                strategy={verticalListSortingStrategy}>
                                {kitOptionItems.map((kitOptionItem) => {
                                    const openOverride = accordionStates[kitOptionItem.uuid as string] || false;

                                    // Get sibling items that have sub-options to copy from
                                    const siblingItemsWithSubOptions = kitOptionItems.filter(
                                        (item) =>
                                            item.uuid !== kitOptionItem.uuid &&
                                            item.require_sub_option &&
                                            item.Sub_Kit_Builder_Option_Items?.some((sub) => !sub.deleteFlag)
                                    );

                                    // console.log(`openOverride ${kitOptionItem.uuid}`, openOverride);
                                    return (
                                        <div key={kitOptionItem.uuid} className={"mt-4"}>
                                            <CatalogItemKitBuilderOptionItem
                                                item={kitOptionItem}
                                                catalogGuid={props.catalogGuid}
                                                updateFn={updateFn}
                                                removeOptionItemFn={removeOptionItemFn}
                                                id={kitOptionItem.uuid as string}
                                                isActive={activeItemId === kitOptionItem.uuid}
                                                openOverride={openOverride}
                                                accordionFn={(item) => accordionToggledFn(item)}
                                                siblingItems={siblingItemsWithSubOptions}
                                                copySubOptionsFn={handleCopySubOptions}
                                            />
                                        </div>
                                    );
                                })}
                            </SortableContext>
                            {/* Drag overlay for option items */}
                            <DragOverlay adjustScale={true}>
                                {activeItem && (
                                    <div className="drag-overlay-item">
                                        <CatalogItemKitBuilderOptionItem
                                            item={activeItem}
                                            catalogGuid={props.catalogGuid}
                                            updateFn={() => {}}
                                            removeOptionItemFn={() => {}}
                                            id={activeItem.uuid as string}
                                            isDragOverlay={true}
                                            openOverride={false}
                                        />
                                    </div>
                                )}
                            </DragOverlay>
                        </DndContext>
                    </div>
                </div>
            </OwAccordion>
        </div>
    );
};
